import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useGameStore } from "../store";
import { Users, Clock, Sparkles, Star, Zap } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { VitrineKiosk } from "./VitrineKiosk";

// Particules décoratives kawaii
function FloatingStars() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-star"
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: [0.3, 0.8, 0.3],
            y: [-10, -30, -10],
            x: [0, Math.sin(i) * 15, 0],
          }}
          transition={{
            duration: 3 + i * 0.5,
            repeat: Infinity,
            delay: i * 0.4,
          }}
          style={{
            left: `${10 + i * 12}%`,
            top: `${20 + (i % 3) * 25}%`,
            fontSize: `${14 + (i % 3) * 8}px`,
          }}
        >
          {i % 3 === 0 ? "✦" : i % 3 === 1 ? "♪" : "✧"}
        </motion.div>
      ))}
    </div>
  );
}

export function AutoRoom() {
  const {
    createRoom,
    startGame,
    players,
    roomCode,
    loading,
    socket,
    joinAsSpectator,
    rehostRoom,
  } = useGameStore();

  const [showQR, setShowQR] = useState(false);
  const [, setCopied] = useState(false);
  const [lanOrigin, setLanOrigin] = useState("");
  const [countdown, setCountdown] = useState<number | null>(null);
  const [quizTitle, setQuizTitle] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionSetId = searchParams.get("questions") || undefined;
  // ?vitrine=<userId> : on suit la schedule de ce user (session active courante).
  // Coexiste avec ?questions=<setId> (set ponctuel) ; mutuellement exclusifs côté URL.
  const scheduleUserId = searchParams.get("vitrine") || undefined;

  // Charger le titre du quiz courant :
  // - via le set ponctuel si ?questions=<id>
  // - via la session active si ?vitrine=<userId>
  useEffect(() => {
    if (questionSetId) {
      fetch(`/api/public/questions/${questionSetId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.title) setQuizTitle(data.title);
        })
        .catch(() => {});
      return;
    }
    if (scheduleUserId) {
      fetch(`/api/public/schedules/${scheduleUserId}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.currentSession?.title) setQuizTitle(data.currentSession.title);
        })
        .catch(() => {});
    }
  }, [questionSetId, scheduleUserId]);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const res = await fetch("/api/local-ip");
        const data = await res.json();
        if (data.ip && mounted) {
          const host = data.ip.startsWith("http") ? data.ip : `http://${data.ip}`;
          // Omettre le port pour les ports HTTP/HTTPS standards (URL plus propre dans le QR)
          const port = Number(data.port);
          const portSuffix = port === 80 || port === 443 ? "" : `:${data.port}`;
          setLanOrigin(`${host}${portSuffix}`);
        }
      } catch {
        /* fallback */
      }
      await createRoom(true, questionSetId, scheduleUserId);
      if (mounted) setShowQR(true);
    };
    init();
    return () => { mounted = false; };
  }, [createRoom]);

  useEffect(() => {
    if (!socket) return;
    const handler = ({ remaining }: { remaining: number }) => {
      setCountdown(remaining > 0 ? remaining : null);
    };
    socket.on("countdown", handler);
    return () => { socket.off("countdown", handler); };
  }, [socket]);

  // Auto-réparation du QR après une reconnexion socket.
  // Le kiosque reste des heures sur l'écran idle et il est SEUL dans sa room ;
  // au moindre disconnect (wifi, onglet throttlé, conteneur redémarré) le serveur
  // GC la room vide. Le QR, lui, continuait d'annoncer le code mort : chaque joueur
  // qui scannait recevait 'roomError' et restait seul dans un lobby fantôme — d'où
  // "deux personnes scannent le même QR et ne sont pas dans la même room".
  // On reprend donc le MÊME code à chaque reconnexion : le QR affiché reste valide.
  // 'reconnect' vient du Manager, il ne se déclenche pas sur la connexion initiale
  // (celle-là est déjà couverte par createRoom).
  useEffect(() => {
    if (!socket) return;
    const onReconnect = () => rehostRoom(questionSetId, scheduleUserId);
    socket.io.on("reconnect", onReconnect);
    return () => { socket.io.off("reconnect", onReconnect); };
  }, [socket, rehostRoom, questionSetId, scheduleUserId]);

  // Propager les paramètres URL utiles dans les navigations (vers /spectate et URL de join).
  // questions= / vitrine= sont cruciaux : sans eux, la boucle kiosque retombe sur les
  // questions par défaut après la fin d'une partie (SpectatorView redirige vers /auto-room
  // avec les params qu'il a).
  const rendererParam = searchParams.get("renderer");
  const rendererSuffix = rendererParam ? `&renderer=${rendererParam}` : "";
  const questionsSuffix = questionSetId ? `&questions=${questionSetId}` : "";
  const vitrineSuffix = scheduleUserId ? `&vitrine=${scheduleUserId}` : "";
  const extraSuffix = `${rendererSuffix}${questionsSuffix}${vitrineSuffix}`;

  useEffect(() => {
    if (!socket || !roomCode) return;
    const handler = () => {
      navigate(`/spectate?room=${roomCode}${extraSuffix}`);
    };
    socket.on("gameStarted", handler);
    return () => { socket.off("gameStarted", handler); };
  }, [socket, roomCode, navigate, extraSuffix]);

  const baseOrigin = lanOrigin || window.location.origin;
  const joinUrl = roomCode ? `${baseOrigin}/?room=${roomCode}${rendererSuffix}` : "";

  const copyToClipboard = async () => {
    if (!roomCode) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.open(`/?room=${roomCode}`, "_blank");
    }
  };

  const handleContinueToGame = async () => {
    if (!roomCode) return;
    await startGame();
    navigate(`/spectate?room=${roomCode}${extraSuffix}`);
  };

  const handleSpectate = () => {
    if (roomCode) {
      window.open(`/spectate?room=${roomCode}${extraSuffix}`, "_blank");
    }
  };

  const handleTestMulti = () => {
    if (roomCode) {
      const qp = new URLSearchParams({ room: roomCode, count: "4" });
      const qs = searchParams.get("questions");
      if (qs) qp.set("questions", qs);
      if (scheduleUserId) qp.set("vitrine", scheduleUserId);
      if (rendererParam) qp.set("renderer", rendererParam);
      // Ouvrir en popup séparée (facile à glisser sur un 2e écran)
      const w = 1200, h = 800;
      const left = window.screenX + window.outerWidth;
      const top = window.screenY;
      window.open(
        `/test-multi.html?${qp.toString()}`,
        "test4p",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
      );
    }
  };

  // Gamepad
  useEffect(() => {
    let lastButtonPress = 0;
    let rafId: number;
    const DEBOUNCE_TIME = 1500;
    const handleGamepad = async () => {
      const gamepads = navigator.getGamepads();
      for (const gamepad of gamepads) {
        if (gamepad?.buttons[1]?.pressed) {
          const now = Date.now();
          if (now - lastButtonPress > DEBOUNCE_TIME) {
            lastButtonPress = now;
            try {
              await joinAsSpectator(roomCode);
              navigate(`/spectate?room=${roomCode}${extraSuffix}`);
            } catch (err) {
              console.error("Failed to join as spectator via gamepad:", err);
            }
          }
        }
      }
    };
    const gamepadLoop = () => { handleGamepad(); rafId = requestAnimationFrame(gamepadLoop); };
    rafId = requestAnimationFrame(gamepadLoop);
    return () => { cancelAnimationFrame(rafId); };
  }, [joinAsSpectator, navigate, roomCode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement)?.tagName === "INPUT") return;
      if (event.key === "s" || event.key === "S") handleSpectate();
      if (event.key === "t" || event.key === "T") handleTestMulti();
      if (event.key === "c" || event.key === "C") copyToClipboard();
      if (event.key === "r" || event.key === "R") window.location.reload();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [roomCode]);

  // Mode kiosque : layout plein écran ultra-dynamique pour TOUT set custom
  // (set ponctuel via ?questions= OU schedule user via ?vitrine=<userId>).
  // Donne la même intro "Scan to play" à toute partie partagée via QR.
  if (questionSetId || scheduleUserId) {
    return (
      <VitrineKiosk
        joinUrl={joinUrl}
        countdown={countdown}
        questionSetId={questionSetId}
        scheduleUserId={scheduleUserId}
      />
    );
  }

  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Grille de logos en fond, rotation 45° */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.25]"
        style={{
          backgroundImage: "url(/assets/bg_grid.svg)",
          backgroundSize: "200px 200px",
          backgroundRepeat: "repeat",
        }}
      />

      {/* Background blobs décoratifs */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-sakura-200/40 blur-3xl" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-lavender-200/40 blur-3xl" />
      <div className="absolute top-[30%] right-[10%] w-[300px] h-[300px] rounded-full bg-sky-200/40 blur-3xl" />

      <FloatingStars />

      <div className="w-full max-w-7xl relative z-10">
        {/* Logo au-dessus du cadre */}
        <motion.div
          className="flex justify-center mb-4"
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
          <motion.img
            src="/assets/logo.svg"
            alt="Logo"
            className="w-56 h-28"
            animate={{ rotate: [-15, 15, -15] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>

        {/* Main card — ratio 16/9 */}
        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-6 lg:p-8 w-full aspect-video flex flex-col"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.4, delay: 0.1 }}
        >
          {/* Titre du quiz + steps */}
          <motion.div
            className="text-center mb-4 shrink-0"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", bounce: 0.5, delay: 0.2 }}
          >
            <h1 className="text-2xl lg:text-4xl font-black text-sakura-500">
              {quizTitle ? (
                <>
                  <span className="text-lavender-400">Quiz du jour : </span>
                  {quizTitle}
                </>
              ) : (
                "Quiz du jour"
              )}
            </h1>
            <p className="text-gray-500 font-semibold mt-2 text-sm lg:text-base">
              Défie tes amis en temps réel ! Plus tu réponds vite, plus ton personnage sprinte. Qui arrivera premier ?
            </p>
            <div className="flex items-center justify-center gap-4 lg:gap-8 mt-3 text-sm lg:text-base font-bold">
              <span className="flex items-center gap-1.5 text-sakura-400">
                <span className="w-6 h-6 rounded-full bg-sakura-400 text-white text-xs flex items-center justify-center font-black shrink-0">1</span>
                Scanne le QR
              </span>
              <span className="text-gray-300">—</span>
              <span className="flex items-center gap-1.5 text-lavender-400">
                <span className="w-6 h-6 rounded-full bg-lavender-400 text-white text-xs flex items-center justify-center font-black shrink-0">2</span>
                Choisis ton pseudo
              </span>
              <span className="text-gray-300">—</span>
              <span className="flex items-center gap-1.5 text-mint-500">
                <span className="w-6 h-6 rounded-full bg-mint text-white text-xs flex items-center justify-center font-black shrink-0">3</span>
                Réponds vite et sprinte !
              </span>
            </div>
          </motion.div>

          <div className="h-2 shrink-0" />

          {/* Loading state */}
          {!showQR || loading || !roomCode ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="inline-block mb-4"
              >
                <Sparkles className="w-16 h-16 text-sakura-400" />
              </motion.div>
              <h2 className="text-3xl font-extrabold text-sakura-500 mb-2">
                Creating room...
              </h2>
              <p className="text-lavender-400 font-semibold">
                Preparing something awesome!
              </p>
            </motion.div>
          ) : (
            <div className="flex-1 flex gap-6 lg:gap-8 flex-col lg:flex-row min-h-0">
              {/* Left: QR code */}
              <div className="lg:w-2/5 bg-gradient-to-br from-sakura-50 to-lavender-50 p-4 lg:p-6 rounded-kawaii flex flex-col items-center border-2 border-sakura-100">
                <motion.div
                  className="flex items-center gap-2 mb-3"
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Zap className="w-5 h-5 text-star" />
                  <p className="text-lg font-bold text-sakura-500">Scan to join!</p>
                  <Zap className="w-5 h-5 text-star" />
                </motion.div>

                <motion.div
                  className="p-5 bg-white rounded-2xl shadow-kawaii mb-4 animate-pulse-glow"
                  whileHover={{ scale: 1.03, rotate: 1 }}
                >
                  <QRCodeSVG
                    value={joinUrl}
                    size={340}
                    level="H"
                    includeMargin={true}
                    fgColor="#E91E6C"
                    bgColor="#FFFFFF"
                  />
                </motion.div>

                {/* Room code badge */}
                <motion.div
                  className="bg-gradient-to-r from-sakura-400 to-lavender-400 text-white px-6 py-2 rounded-full shadow-kawaii mb-4"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <p className="text-xs font-semibold opacity-80">Room Code</p>
                  <p className="text-xl font-black tracking-widest">{roomCode}</p>
                </motion.div>

                {/* Boutons cachés — accessibles via raccourcis clavier (T=Test4P, C=Copy, S=Spectate) */}
                <div className="hidden">
                  <button onClick={copyToClipboard}>Copy</button>
                  <button onClick={handleSpectate}>Spectate</button>
                  <button onClick={handleTestMulti}>Test 4P</button>
                </div>
              </div>

              {/* Right: players + countdown */}
              <div className="lg:w-3/5 flex flex-col min-h-0">
                {/* Countdown */}
                {countdown !== null && (
                  <motion.div
                    className="mb-4 flex flex-col items-center bg-gradient-to-r from-star/20 to-sakura-100 rounded-kawaii p-4 border-2 border-star/30 shrink-0"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                  >
                    <div className="flex items-center gap-2 text-sakura-600 mb-1">
                      <Clock className="w-5 h-5" />
                      <span className="text-lg font-bold">Game starts in</span>
                    </div>
                    <motion.span
                      className="text-5xl font-black text-sakura-500 animate-countdown-pulse"
                      key={countdown}
                      initial={{ scale: 1.3, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", bounce: 0.6 }}
                    >
                      {countdown}
                    </motion.span>
                  </motion.div>
                )}

                {/* Players header */}
                <div className="flex items-center justify-between mb-3 shrink-0">
                  <h3 className="text-xl font-extrabold text-lavender-500 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Players
                  </h3>
                  <span className="bg-lavender-100 text-lavender-500 px-3 py-1 rounded-full text-sm font-bold">
                    {players.length} {players.length === 1 ? "player" : "players"}
                  </span>
                </div>

                {/* Player list — scrollable */}
                <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
                  <AnimatePresence>
                    {players.map((player, i) => (
                      <motion.div
                        key={player.id}
                        initial={{ opacity: 0, x: -30, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 30 }}
                        transition={{ type: "spring", bounce: 0.5, delay: i * 0.1 }}
                        className="bg-white p-3 rounded-xl flex items-center justify-between shadow-kawaii-card border-2 border-lavender-100/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sakura-300 to-lavender-300 flex items-center justify-center text-white font-black text-base shadow-sm">
                            {player.name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-bold text-gray-700">
                            {player.name}
                          </span>
                        </div>
                        <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                          player.ready
                            ? "bg-mint/20 text-emerald-600"
                            : "bg-gray-100 text-gray-400"
                        }`}>
                          {player.ready ? "Ready!" : "Waiting..."}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Empty state */}
                {players.length === 0 && (
                  <motion.div
                    className="flex-1 flex flex-col items-center justify-center"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Star className="w-10 h-10 text-star mb-3 animate-wiggle" />
                    <p className="text-lavender-400 font-bold">
                      Waiting for players to scan...
                    </p>
                  </motion.div>
                )}

                {/* Start button */}
                <motion.button
                  whileHover={{ scale: 1.03, y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleContinueToGame}
                  className="mt-4 w-full py-3 px-6 text-white font-black text-lg bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg hover:shadow-kawaii transition-all shrink-0"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Start Now!
                    <Sparkles className="w-5 h-5" />
                  </span>
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
